import { IconForAttachmentCitation } from "@app/components/assistant/conversation/attachment/utils";
import { useFilePreviewContextFacultatif } from "@app/components/assistant/conversation/FilePreviewContext";
import {
  fichierDustDuLien,
  typePourApercu,
} from "@app/lib/compta/fichier_dust";
import { baseDuChat, destinationCitation } from "@app/lib/compta/lien_citation";
import { estIntegre } from "@app/lib/compta/mode_integre";
import { envoyer } from "@app/lib/compta/navigation_app";
import React, { useEffect } from "react";
import type { ReactMarkdownProps } from "react-markdown/lib/complex-types";
import { visit } from "unist-util-visit";

import type { MCPReferenceCitation } from "./MCPReferenceCitation";

type CitationsContextType = {
  references: {
    [key: string]: MCPReferenceCitation;
  };
  updateActiveReferences: (doc: MCPReferenceCitation, index: number) => void;
};

export const CitationsContext = React.createContext<CitationsContextType>({
  references: {},
  updateActiveReferences: () => null,
});

function isCiteProps(props: ReactMarkdownProps): props is ReactMarkdownProps & {
  references: string;
} {
  return Object.prototype.hasOwnProperty.call(props, "references");
}

export function CiteBlock(props: ReactMarkdownProps) {
  const { references, updateActiveReferences } =
    React.useContext(CitationsContext);
  // Aperçu des documents (fork, 03/10) : une citation vers un fichier Dust du workspace s'ouvre dans
  // le panneau d'aperçu de la conversation, comme un fichier joint, au lieu d'un nouvel onglet.
  const apercu = useFilePreviewContextFacultatif();
  const refs =
    isCiteProps(props) && props.references
      ? (
          JSON.parse(props.references) as {
            counter: number;
            ref: string;
          }[]
        ).filter((r) => r.ref in references)
      : undefined;

  const referencesString =
    isCiteProps(props) && props.references ? props.references : undefined;

  useEffect(() => {
    if (refs) {
      refs.forEach((r) => {
        const document = references[r.ref];
        updateActiveReferences(document, r.counter);
      });
    }
    // oxlint-disable-next-line react/exhaustive-deps -- not reported by the previous linter; deps kept as-is
  }, [referencesString, references, updateActiveReferences]);

  if (refs) {
    return (
      <span className="inline-flex space-x-1">
        {refs.map((r, i) => {
          const document = references[r.ref];
          const link = document.href;
          // compta (30/09) : une citation sans lien ne mene nulle part ; un lien vers l'app navigue
          // dans l'app quand le chat est integre (le parent recoit l'intention), sinon nouvel onglet.
          const destination = destinationCitation(
            link,
            typeof window === "undefined" ? "" : window.location.origin,
            baseDuChat(process.env.NEXT_PUBLIC_DUST_APP_URL)
          );
          const puce = (
            <div className="group flex flex-row items-center justify-center space-x-1 rounded-lg bg-muted-background p-1 text-xs font-medium text-muted-foreground">
              <span className="grayscale-[100%] transition-all duration-150 ease-in-out group-hover:grayscale-0">
                <IconForAttachmentCitation
                  provider={document.provider}
                  contentType={document.contentType}
                  nodeType={"document"}
                  sourceUrl={document.href}
                  size="sm"
                ></IconForAttachmentCitation>
              </span>
              <span>{r.counter}</span>
            </div>
          );

          return (
            <div key={`${r.ref}-${i}`}>
              {destination.type === "aucune" ? (
                puce
              ) : (
                <a
                  href={
                    destination.type === "app"
                      ? destination.route
                      : destination.href
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    const fichier =
                      apercu?.canPreview && destination.type !== "app"
                        ? (document.fileId ??
                          (apercu.workspaceId
                            ? fichierDustDuLien(link, apercu.workspaceId)
                            : null))
                        : null;
                    if (apercu && fichier) {
                      e.preventDefault();
                      apercu.openFilePreview({
                        fileId: fichier,
                        contentType: typePourApercu(
                          document.contentType,
                          document.title
                        ),
                      });
                      return;
                    }
                    if (
                      destination.type === "app" &&
                      typeof window !== "undefined" &&
                      estIntegre(window)
                    ) {
                      e.preventDefault();
                      envoyer({ type: "naviguer", route: destination.route });
                    }
                  }}
                >
                  {puce}
                </a>
              )}
            </div>
          );
        })}
      </span>
    );
  } else {
    const { children } = props;
    return <sup>{children}</sup>;
  }
}

export function getCiteDirective() {
  // Initialize a counter to keep track of citation references, starting from 1.
  let refCounter = 1;
  const refSeen: { [ref: string]: number } = {};

  const counter = (ref: string) => {
    if (!refSeen[ref]) {
      refSeen[ref] = refCounter++;
    }
    return refSeen[ref];
  };

  return () => {
    return (tree: any) => {
      visit(tree, ["textDirective"], (node) => {
        if (node.name === "cite" && node.children[0]?.value) {
          const data = node.data || (node.data = {});

          const references = node.children[0]?.value
            .split(",")
            .map((s: string) => s.trim())
            // Citations used to be 2 characters long, but are now 3 characters long.
            // We support both for backward compatibility.
            .filter((s: string) => s.length === 2 || s.length === 3)
            .map((ref: string) => ({
              counter: counter(ref),
              ref,
            }));

          // `sup` will then be mapped to a custom component `CiteBlock`.
          data.hName = "sup";
          data.hProperties = {
            references: JSON.stringify(references),
          };
        }
      });
    };
  };
}
